import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import * as authApi from '@/api/auth'
import { useAuth } from '@/auth/AuthContext'

export function ChangePassword() {
  const { user, loading, refreshUser, requireMfa, mfaEnabled } = useAuth()
  const navigate = useNavigate()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-[#eef2f6] to-[#f8fafc] p-4">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (!user.must_change_password) {
    if (requireMfa && !mfaEnabled) {
      return <Navigate to="/mfa/setup" replace />
    }
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.')
      return
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from the temporary password.')
      return
    }
    setSubmitting(true)
    try {
      await authApi.changePassword(currentPassword, newPassword)
      const updated = await refreshUser()
      if (updated.require_mfa && !updated.totp_enabled) {
        navigate('/mfa/setup', { replace: true })
      } else {
        navigate('/', { replace: true })
      }
    } catch (err) {
      const detail = err.response?.data?.detail
      setError(
        typeof detail === 'string'
          ? detail
          : 'Could not change password. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const inputClass =
    'mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary'

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-[#eef2f6] to-[#f8fafc] p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 shadow-sm">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
            BD
          </span>
          <p className="text-lg font-semibold tracking-tight text-foreground">Bekat Dash</p>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          You are using a temporary password. Choose a new password before continuing.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <div>
            <label htmlFor="current" className="block text-sm font-medium text-foreground">
              Temporary password
            </label>
            <input
              id="current"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              minLength={8}
              disabled={submitting}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="new" className="block text-sm font-medium text-foreground">
              New password
            </label>
            <input
              id="new"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              disabled={submitting}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="confirm" className="block text-sm font-medium text-foreground">
              Confirm new password
            </label>
            <input
              id="confirm"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              disabled={submitting}
              className={inputClass}
            />
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
          >
            {submitting ? 'Saving…' : 'Set new password'}
          </button>
        </form>
      </div>
    </div>
  )
}
